import { useEffect, useState } from "react";
import { Chart } from "react-chartjs-2";
import {
	Chart as ChartJS,
	LineController,
	LineElement,
	BarController,
	BarElement,
	PointElement,
	LinearScale,
	TimeScale,
	Tooltip,
	Legend
} from "chart.js";
import "chartjs-adapter-date-fns";
import { VolumeDataEntry } from "../../interfaces/interfaces";
import { runPeakValleyAnalysis } from "../../functions/api/tank_levels";
import TransactionTable from "./TableComponent";

ChartJS.register(
	LineController,
	LineElement,
	BarController,
	BarElement,
	PointElement,
	LinearScale,
	TimeScale,
	Tooltip,
	Legend
);

type VolumeChartProps = {
	data: VolumeDataEntry[];
	tankCode: string;
};

export default function VolumeChart({ data, tankCode }: VolumeChartProps) {
	const [chartData, setChartData] = useState<any>(null);
	const [peakValleyResults, setPeakValleyResults] = useState<any[]>([]);
	const [analysisRun, setAnalysisRun] = useState(false); // Optional: flag to show table only after analysis

	useEffect(() => {
		if (!Array.isArray(data) || data.length === 0) return;

		const transformVolumeData = (data: VolumeDataEntry[]) => {
			return data.map(entry => ({
				baseTime: new Date(entry.baseTime),
				nearbyTime: new Date(entry.nearbyTime),
				receiveGross: entry.receive_gross_volume,
				receiveGST: entry.receive_gst_volume,
				tankGross: entry.tank_gross_volume,
				tankGST: entry.tank_GST_volume,
				water: entry.water_level_mm
			}));
		};

		const formattedData = transformVolumeData(data);

		const receiveDataset = formattedData
			.map(({ baseTime, receiveGST }) => ({
				x: baseTime.toISOString(),
				y: receiveGST
			}))
			.filter((item, index, self) =>
				index === self.findIndex(t => t.x === item.x && t.y === item.y)
			);

		const tankGrossDataset = formattedData.map(({ nearbyTime, tankGross }) => ({
			x: nearbyTime.toISOString(),
			y: tankGross
		}));

		const tankGSTDataset = formattedData.map(({ nearbyTime, tankGST }) => ({
			x: nearbyTime.toISOString(),
			y: tankGST
		}));

		const waterDataset = formattedData.map(({ nearbyTime, water }) => ({
			x: nearbyTime.toISOString(),
			y: water*100
		}));
		setChartData({
			datasets: [
				{
					label: "Receive GST Volume",
					type: "bar",
					data: receiveDataset,
					borderColor: "blue",
					backgroundColor: "rgba(4, 8, 40, 0.5)",
					tension: 0.3,
					barThickness: 2
				},
				{
					label: "Tank Gross Volume",
					type: "line",
					data: tankGrossDataset,
					backgroundColor: "rgba(0, 128, 0, 0.5)",
					borderColor: "green",
					borderWidth: 1
				},
				{
					label: "Tank GST Volume",
					type: "line",
					data: tankGSTDataset,
					backgroundColor: "rgba(38, 9, 67, 0.5)",
					borderColor: "purple",
					borderWidth: 1
				},
				{
					label: "Water Level (×10⁻² mm)",
					type: "line",
					data: waterDataset,
					backgroundColor: "rgba(39, 106, 193, 0.5)",
					borderColor: "blue",
					borderWidth: 1
				}
			]
		});
	}, [data]);

	const analyseLevelData = async (data: VolumeDataEntry[], tankCode: string) => {
		try {
			const results = await runPeakValleyAnalysis(data, tankCode);
			// console.log("Got HEEERRREEE")
			console.log(results.data)
			setPeakValleyResults(Array.isArray(results.data) ? results.data : []);
			setAnalysisRun(true);
		} catch (error) {
			console.error("Peak-valley analysis failed:", error);
			setPeakValleyResults([]);
			setAnalysisRun(false);
		}
	};

	return chartData ? (
		<div>
			<Chart
				type="line"
				data={chartData}
				options={{
					responsive: true,
					scales: {
						x: {
							type: "time",
							time: { unit: "hour" },
							title: { display: true, text: "Timestamp" }
						},
						y: {
							beginAtZero: true,
							title: { display: true, text: "Volume" }
						}
					}
				}}
			/>

			<button className="button" onClick={() => analyseLevelData(data, tankCode)}>
				Analyse
			</button>

			{analysisRun && peakValleyResults.length > 0 ? (
				<div className="table-scroll-inner">
					<TransactionTable data={peakValleyResults} />
				</div>
			) : (
				analysisRun && <div>No peak-valley results found.</div>
			)}
		</div>
	) : (
		<div>Loading chart…</div>
	);
}